import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil, pathOr, propOr, last } from 'ramda'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { canEditBp } from 'utils/user'
import moment from 'moment'
import RemoveAccessConfirm from 'features/bpDetails/Components/files/RemoveAccessConfirm'
import saveFilePermissions, { permissionsUnchanged, removedUserIds } from 'features/bpDetails/utils/saveFilePermissions'
import { removeFileRoutine } from 'ducks/files/actions'
import { ReactComponent as FileIcon } from 'assets/images/file-icon.svg'
import { formatDate } from 'utils/date'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { Chip, Tooltip } from '@mui/material'
import UnshareFileModal from 'features/bpDetails/Components/UnshareFileModal'
import Modal from 'components/atoms/Modal'
import EditFileModal from 'features/bpDetails/Components/EditFileModal'
import { getFloorOptions } from 'utils/bpData'
import ShareFileModal from 'features/bpDetails/Components/ShareFileModal'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import styled, { css } from 'styled-components'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { isNotNilOrEmpty } from 'utils/ramda'
import AddUsersToFile from 'features/bpDetails/Components/files/AddUsersToFile'
import ImageViewer from 'components/ImageViewer'
import { markFileAsViewed, getFileUrls } from 'services/fileService'
import ViewedStatusModal from 'features/bpDetails/Components/files/ViewedStatusModal'
import IdentityRestrictedNote from 'features/bpDetails/Components/files/IdentityRestrictedNote'

const FileMinified = ({ file, isShared, callback, hideOptions }) => {
  const { uploader, expirationDate, displayName } = file
  const [anchorEl, setAnchorEl] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [viewedStatusModalOpen, setViewedStatusModalOpen] = useState(false)
  const open = Boolean(anchorEl)
  const dispatch = useDispatch()
  const [pendingRemoval, setPendingRemoval] = useState(null)
  const [removalBusy, setRemovalBusy] = useState(false)
  const hasExpirationDate = useMemo(
    () => !isNil(expirationDate),
    [expirationDate]
  )
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [imageViewerOpen, setImageViewerOpen] = useState(false)
  const [fileUrls, setFileUrls] = useState(null)
  const [isLoadingUrls, setIsLoadingUrls] = useState(false)
  const bp = useSelector(getSelectedBp)
  const currentUser = useSelector(getCurrentUser)
  const isFloorPlan = pathOr('', ['fileType', 'name'], file) === 'Building plan'
  const canBeAccessed = file?.canBeAccessed || false

  const latestVersionCreationDate = useMemo(() => {
    const versions = propOr([], 'versions', file).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt)
    )

    return last(versions)?.createdAt || null
  }, [file])

  const handlePdfViewerClose = () => setPdfViewerOpen(false)
  const handleImageViewerClose = () => setImageViewerOpen(false)
  const canEdit = canEditBp(currentUser, bp)

  const isExpired = useMemo(() => {
    return hasExpirationDate
      ? moment(expirationDate).isSameOrBefore(moment())
      : false
  }, [hasExpirationDate])

  const openMenu = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }
  const handleClose = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)
  }

  const handleModalOpen = e => {
    e.stopPropagation()
    e.preventDefault()
    setModalOpen(true)
  }
  const handleModalClose = () => {
    callback()
    setModalOpen(false)
  }

  const handleEditModalOpen = e => {
    e.stopPropagation()
    e.preventDefault()
    setEditModalOpen(true)
  }
  const handleEditModalClose = () => {
    callback()
    setEditModalOpen(false)
  }

  const handleShareModalOpen = e => {
    e.stopPropagation()
    e.preventDefault()
    setAnchorEl(null)
    setShareModalOpen(true)
  }
  const handleShareModalClose = () => {
    callback()
    setShareModalOpen(false)
  }

  const handleViewedStatusModalOpen = e => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }

    setAnchorEl(null)
    setViewedStatusModalOpen(true)
  }

  const handleRemoveFile = useCallback(() => {
    dispatch(removeFileRoutine({ id: file.id, callback, bpId: bp.id }))
    handleModalClose()
  }, [file])

  const handlePreviewFile = async e => {
    e.stopPropagation()
    e.preventDefault()

    // Get URLs - either from cached state, file object, or fetch on demand
    let url = fileUrls?.url || file.url

    // If URL not available, fetch it
    if (!url && bp?.id) {
      try {
        setIsLoadingUrls(true)
        const response = await getFileUrls(bp.id, file.id)
        const urls = response.data
        url = urls.url
        setFileUrls(urls)
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    }

    if (!url) return

    if (/\.pdf/.test(url.toLowerCase())) {
      setPdfViewerOpen(true)
    } else if (
      /\.jpg|\.jpeg|\.png|\.svg|\.webp|\.gif/.test(url.toLowerCase())
    ) {
      setImageViewerOpen(true)
    } else {
      window.open(url, '_blank')
    }
  }

  const handleDownloadFile = async (e, downloadOriginal = false) => {
    e.stopPropagation()
    e.preventDefault()

    // Get URL - either from state, file object, or fetch on demand
    let url = downloadOriginal
      ? (fileUrls?.originalUrl || file.originalUrl)
      : (fileUrls?.url || file.url)

    // If URL not available, fetch it
    if (!url && bp?.id) {
      try {
        setIsLoadingUrls(true)
        const response = await getFileUrls(bp.id, file.id)
        const urls = response.data
        setFileUrls(urls)
        url = downloadOriginal ? urls.originalUrl : urls.url
      } catch (error) {
        console.error('Failed to fetch file URLs:', error)
        return
      } finally {
        setIsLoadingUrls(false)
      }
    }

    if (!url) return

    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = '_blank'
    anchor.download = file.displayName
    anchor.click()

    const fileId = file.id
    const fileVersionId = pathOr('', ['versions', 0, 'id'], file)

    markFileAsViewed({
      fileId,
      fileVersionId,
      type: 'download'
    })
  }

  const originalUrl = fileUrls?.originalUrl || propOr('', 'originalUrl', file)
  const hasOriginalUrl = isNotNilOrEmpty(originalUrl)

  // Create a file object with fetched URLs merged in for viewers
  const fileWithUrls = useMemo(() => {
    if (!fileUrls) return file
    return {
      ...file,
      url: fileUrls.url || file.url,
      originalUrl: fileUrls.originalUrl || file.originalUrl,
      thumbnailUrl: fileUrls.thumbnailUrl || file.thumbnailUrl
    }
  }, [file, fileUrls])

  const finishSave = async selectedUsers => {
    await saveFilePermissions({ bpId: bp.id, file, selectedUsers, dispatch })
    setTimeout(() => {
      callback()
    }, 1000)
  }

  const handleSavePermissions = selectedUsers => {
    if (permissionsUnchanged(file, selectedUsers)) return true
    if (removedUserIds(file, selectedUsers).length === 0) {
      finishSave(selectedUsers)
      return true
    }
    return new Promise(resolve => {
      setPendingRemoval({ selectedUsers, resolve })
    })
  }

  const confirmRemoval = async () => {
    setRemovalBusy(true)
    try {
      await finishSave(pendingRemoval.selectedUsers)
    } finally {
      setRemovalBusy(false)
      pendingRemoval.resolve(true)
      setPendingRemoval(null)
    }
  }

  const cancelRemoval = () => {
    pendingRemoval.resolve(false)
    setPendingRemoval(null)
  }

  return (
    <>
      <FileWrapper
        key={file.id}
        id={file.id}
        canBeAccessed={canBeAccessed}
        isExpired={isExpired}
        hasIdentityNote={file.isIdentityRestricted || file.isIdentityUnreadable}
        onClick={canBeAccessed ? handlePreviewFile : () => {}}
      >
        <NameWrapper>
          <FileIconWrapper>
            <FileIcon />
          </FileIconWrapper>
          <FileNameWithScore>
            <FileName>{displayName}</FileName>
            {file.searchScore && (
              <Tooltip title="AI Semantic Search Relevance Score" placement="top">
                <SearchScoreChip
                  label={`${Math.round(file.searchScore * 100)}% match`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </FileNameWithScore>
        </NameWrapper>
        {(file.isIdentityRestricted || file.isIdentityUnreadable) && (
          <IdentityNoteRow>
            <IdentityRestrictedNote pending={file.isIdentityPending} unreadable={file.isIdentityUnreadable} />
          </IdentityNoteRow>
        )}
        <FileDetailsWrapper>
          <DetailsWrapper>
            <Details>
              <p>
                {hasExpirationDate && (
                  <ExpiryDate isExpired={isExpired}>
                    {isExpired ? `Expired at:` : `Expiry date:`}&nbsp;
                    {formatDate(expirationDate)}
                  </ExpiryDate>
                )}
                {latestVersionCreationDate && (
                  <span>
                    Upload&nbsp;date:&nbsp;
                    {formatDate(latestVersionCreationDate)}
                  </span>
                )}
              </p>
              {uploader && (
                <p>
                  Upload by:{' '}
                  {propOr('', 'firstName', uploader) +
                    ' ' +
                    propOr('', 'lastName', uploader)}
                </p>
              )}
            </Details>
          </DetailsWrapper>
          {!hideOptions && (
            <IconWrapper onClick={e => e.stopPropagation()}>
              <MoreButton
                type='button'
                aria-label={`More actions for ${file.displayName || file.name || 'this document'}`}
                onClick={canBeAccessed ? openMenu : () => {}}
              >
                <MoreIcon />
              </MoreButton>
              <Menu
                anchorEl={anchorEl}
                style={{
                  marginTop: '10px'
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center'
                }}
                open={open}
                onClose={handleClose}
              >
                <MenuItem onClick={handlePreviewFile}>Preview</MenuItem>
                <MenuItem onClick={e => handleDownloadFile(e, false)}>
                  {hasOriginalUrl ? 'Download PDF' : 'Download'}
                </MenuItem>
                {hasOriginalUrl && (
                  <MenuItem onClick={e => handleDownloadFile(e, true)}>
                    Download original file
                  </MenuItem>
                )}
                {!isShared && canEdit && (
                  <MenuItem onClick={handleEditModalOpen}>
                    Edit file details
                  </MenuItem>
                )}
                {canEdit && (
                  <AddUsersToFile
                    initiallySelected={file?.users || []}
                    onSave={handleSavePermissions}
                    savedCount={file?.users?.length || 0}
                  >
                    <MenuItem>Manage permissions</MenuItem>
                  </AddUsersToFile>
                )}
                {canEdit && (
                  <MenuItem onClick={handleViewedStatusModalOpen}>
                    Views and downloads
                  </MenuItem>
                )}
                <MenuItem disabled={isFloorPlan} onClick={handleShareModalOpen}>
                  Share
                </MenuItem>
                <UnshareFileModal bp={bp} file={file} />
                {!isShared && (
                  <MenuItem onClick={handleModalOpen}>Remove file</MenuItem>
                )}
              </Menu>
            </IconWrapper>
          )}
        </FileDetailsWrapper>
      </FileWrapper>
      <Modal
        open={modalOpen}
        title='Remove file'
        onClose={handleModalClose}
        onCancel={handleModalClose}
        onSubmit={handleRemoveFile}
        withSubmit
        withCancel
      >
        Do you really want to remove this file?
      </Modal>
      <EditFileModal
        open={editModalOpen}
        onClose={handleEditModalClose}
        floorOptions={getFloorOptions(bp)}
        file={file}
      />
      <ShareFileModal
        open={shareModalOpen}
        onClose={handleShareModalClose}
        file={file}
      />
      <PdfFullScreenViewer
        open={pdfViewerOpen}
        onClose={handlePdfViewerClose}
        file={fileWithUrls}
      />
      <ImageViewer
        open={imageViewerOpen}
        onClose={handleImageViewerClose}
        file={fileWithUrls}
      />
      {viewedStatusModalOpen && (
        <ViewedStatusModal
          file={file}
          externalOpen={viewedStatusModalOpen}
          onExternalClose={() => setViewedStatusModalOpen(false)}
        />
      )}
      <RemoveAccessConfirm
        open={Boolean(pendingRemoval)}
        count={pendingRemoval ? removedUserIds(file, pendingRemoval.selectedUsers).length : 0}
        everyone={pendingRemoval ? pendingRemoval.selectedUsers.length === 0 : false}
        busy={removalBusy}
        onConfirm={confirmRemoval}
        onCancel={cancelRemoval}
      />
    </>
  )
}

export default FileMinified

const ExpiryDate = styled.span`
  display: inline-block;
  margin-right: 5px;
  color: ${({ theme, isExpired }) =>
    isExpired ? theme.colors.error : theme.colors.haiti};
`

const DetailsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-right: 10px;
  box-sizing: border-box;
`

const Details = styled.div`
  font-size: 11px;
  text-align: right;
  color: ${({ theme }) => theme.colors.black};
`

const MoreIcon = styled(MoreVertIcon)`
  color: #4d4d56;
  font-size: 26px !important;
`

const MoreButton = styled.button`
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f1f1f3;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 1px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
`

const FileDetailsWrapper = styled.div`
  box-sizing: border-box;
  justify-content: flex-end;
  display: flex;
  max-width: 60%;
`

const FileName = styled.div`
  margin-left: 15px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
`

const FileWrapper = styled.div`
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  min-height: 50px;
  flex-wrap: wrap;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px 4px 0 0;
  transition: all 0.3s;
  opacity: ${({ isExpired }) => (isExpired ? 0.6 : 1)};
  background-color: ${({ isExpired, theme }) =>
    isExpired ? theme.colors.grey[400] : 'transparent'};
  position: relative;
  padding: ${({ hasIdentityNote }) => (hasIdentityNote ? '6px 0' : '0')};

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  ${({ canBeAccessed }) =>
    !canBeAccessed &&
    css`
      color: ${({ theme }) => theme.colors.grey[700]} !important;
      cursor: default !important;

      svg {
        cursor: default !important;
      }

      & div {
        color: ${({ theme }) => theme.colors.grey[700]} !important;
      }
      &:hover {
        border-color: ${({ theme }) => theme.colors.grey[700]};
      }
    `}
`

const NameWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding-left: 15px;
  max-width: 300px;
  overflow: hidden;

  @media (min-width: 1300px) {
    max-width: 500px;
  }
`

const IdentityNoteRow = styled.div`
  flex-basis: 100%;
  box-sizing: border-box;
  padding: 2px 15px 0 15px;
`

const FileNameWithScore = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`

const SearchScoreChip = styled(Chip)`
  && {
    height: 20px;
    font-size: 11px;
    font-weight: 600;
    border-width: 1.5px;

    .MuiChip-label {
      padding: 0 6px;
    }
  }
`
